import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { SEO } from "@/lib/seo";
import * as Icons from "lucide-react";
import {
    AlertTriangle,
    CheckCircle2,
    Printer,
    ArrowRight,
    Info,
    Phone,
    ShieldAlert,
    ShoppingBag,
} from "lucide-react";
import { CLINIC_PHONE } from "@/lib/mailto";
import { getPrepGuide, YOU_KNOW_READY_WHEN, RED_FLAGS, YOU_KNOW_READY_WHEN_FR, RED_FLAGS_FR } from "@/data/prepGuides";
import { useLocalize } from "@/data/localize";

export default function BowelPrepGuidePage() {
    const { t } = useTranslation();
    const { lang, localize } = useLocalize();
    const { prepSlug } = useParams();
    const slug = prepSlug || "pico-salax";
    const raw = getPrepGuide(slug);
    if (!raw) return <Navigate to="/resources/bowel-prep-guide" replace />;
    const guide = localize(raw);
    // Localize steps individually as well
    const steps = (guide.steps || []).map((s) => localize(s));
    const readyList = lang === "fr" ? YOU_KNOW_READY_WHEN_FR : YOU_KNOW_READY_WHEN;
    const redFlagsList = lang === "fr" ? RED_FLAGS_FR : RED_FLAGS;

    return (
        <>
            <SEO title={`${guide.product} — ${t("bowelPrep.chip")}`} description={guide.who_is_this_for} />
            <div className="container-page pt-8">
                <Breadcrumbs
                    items={[
                        { to: "/resources", label: t("resources.crumb") },
                        { to: "/resources/bowel-prep-guide", label: t("bowelPrep.crumb") },
                        { label: guide.product },
                    ]}
                />
            </div>

            <section className="container-page pt-6 pb-4">
                <div className="max-w-3xl">
                    <span className="chip-teal">{t("bowelPrep.detail.prepChip", { product: guide.product })}</span>
                    <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold text-[color:var(--brand-navy)] leading-tight">
                        {guide.headline}
                    </h1>
                    <p className="mt-4 text-[color:var(--text-muted)] leading-relaxed text-lg">
                        {guide.who_is_this_for}
                    </p>
                    <div className="mt-5 flex flex-wrap items-center gap-3">
                        <button
                            onClick={() => window.print()}
                            className="inline-flex items-center gap-1.5 rounded-xl bg-[color:var(--brand-navy)] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[color:var(--brand-navy-2)]"
                            data-testid="bowel-prep-print"
                        >
                            <Printer className="h-4 w-4" />
                            {t("cta.printSavePdf")}
                        </button>
                        <Link
                            to="/resources/bowel-prep-guide"
                            className="inline-flex items-center gap-1.5 rounded-xl border border-[color:var(--border-default)] bg-white px-4 py-2.5 text-sm font-semibold text-[color:var(--brand-navy)] hover:bg-[color:var(--bg-subtle)]"
                            data-testid="bowel-prep-switch"
                        >
                            {t("cta.switchGuide")}
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {guide.contraindications && (
                <section className="container-page pb-4">
                    <div
                        className="card-soft p-5 sm:p-6 flex items-start gap-3"
                        style={{ background: "rgba(180,83,9,0.06)" }}
                        data-testid="bowel-prep-safety"
                    >
                        <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "var(--warning)" }} />
                        <div>
                            <p className="font-semibold text-[color:var(--brand-navy)]">
                                {t("bowelPrep.detail.importantSafetyNote")}
                            </p>
                            <p className="mt-1 text-sm text-[color:var(--text-muted)] leading-relaxed">
                                {guide.contraindications}
                            </p>
                        </div>
                    </div>
                </section>
            )}

            <section className="container-page pb-4">
                <div
                    className="card-soft p-5 sm:p-6 flex items-start gap-3"
                    data-testid="bowel-prep-where-to-buy"
                    style={{ background: "linear-gradient(135deg, rgba(14,124,134,0.10) 0%, rgba(255,255,255,1) 60%)" }}
                >
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-white text-[color:var(--brand-teal-2)] border border-[color:var(--brand-teal-2)]/25 shrink-0">
                        <ShoppingBag className="h-4 w-4" />
                    </span>
                    <div>
                        <p className="text-[11px] uppercase tracking-wider font-semibold text-[color:var(--brand-teal-2)]">
                            {t("bowelPrep.detail.whereChip")}
                        </p>
                        <h3 className="mt-1 font-display text-lg sm:text-xl font-semibold text-[color:var(--brand-navy)]">
                            {t("bowelPrep.detail.whereTitle", { product: guide.product })}
                        </h3>
                        <p className="mt-2 text-sm text-[color:var(--text-muted)] leading-relaxed">
                            {t("bowelPrep.detail.whereBody1", { product: guide.product })}
                        </p>
                        <p className="mt-2 text-sm text-[color:var(--text-muted)] leading-relaxed">
                            {t("bowelPrep.detail.whereBody2", { phone: CLINIC_PHONE })}
                        </p>
                    </div>
                </div>
            </section>

            <section className="container-page pb-6" data-testid="bowel-prep-steps">
                <ol className="space-y-6">
                    {steps.map((s, i) => {
                        const reverse = i % 2 === 1;
                        const StepIcon = (Icons[s.icon] || Info);
                        return (
                            <li
                                key={s.key || s.title}
                                data-testid={`bowel-prep-step-${i + 1}`}
                                className="card-soft overflow-hidden grid gap-0 md:grid-cols-12 items-stretch"
                            >
                                <div className={`md:col-span-5 relative bg-[color:var(--bg-subtle)] ${reverse ? "md:order-2" : ""}`}>
                                    <div className="aspect-square w-full">
                                        <img
                                            src={s.img}
                                            alt=""
                                            className="h-full w-full object-cover"
                                            loading="lazy"
                                            draggable={false}
                                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                                        />
                                    </div>
                                    <span
                                        className="absolute top-4 left-4 inline-flex h-10 w-10 items-center justify-center rounded-full text-white font-semibold text-lg shadow-lg"
                                        style={{ background: "var(--brand-navy)" }}
                                    >
                                        {i + 1}
                                    </span>
                                </div>

                                <div className="md:col-span-7 p-6 sm:p-7 flex flex-col justify-center">
                                    <div className="flex items-center gap-2">
                                        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[color:var(--brand-teal-soft)] text-[color:var(--brand-teal-2)]">
                                            <StepIcon className="h-5 w-5" />
                                        </span>
                                        <p className="text-[11px] uppercase tracking-wider text-[color:var(--brand-teal-2)] font-semibold">
                                            {t("bowelPrep.detail.stepLabel", { n: i + 1 })}
                                        </p>
                                    </div>
                                    <h2 className="mt-3 font-display text-2xl font-semibold text-[color:var(--brand-navy)] leading-tight">
                                        {s.title}
                                    </h2>
                                    <p className="mt-2 text-[color:var(--text-muted)] leading-relaxed">
                                        {s.summary}
                                    </p>
                                    <ul className="mt-3 space-y-2">
                                        {(s.bullets || []).map((b) => (
                                            <li key={b} className="flex items-start gap-2 text-sm text-[color:var(--text-muted)] leading-relaxed">
                                                <CheckCircle2 className="h-4 w-4 text-[color:var(--brand-teal-2)] shrink-0 mt-0.5" />
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </li>
                        );
                    })}
                </ol>
            </section>

            <section className="container-page pb-6">
                <div className="grid gap-5 md:grid-cols-2">
                    <div className="card-soft p-6" style={{ background: "linear-gradient(135deg, rgba(14,124,134,0.10) 0%, rgba(255,255,255,1) 60%)" }}>
                        <div className="flex items-center gap-2">
                            <CheckCircle2 className="h-5 w-5 text-[color:var(--brand-teal-2)]" />
                            <h2 className="font-display text-lg font-semibold text-[color:var(--brand-navy)]">
                                {t("bowelPrep.detail.readyTitle")}
                            </h2>
                        </div>
                        <ul className="mt-3 space-y-2 text-sm text-[color:var(--text-muted)] leading-relaxed pl-5 list-disc marker:text-[color:var(--brand-teal)]">
                            {readyList.map((r) => (<li key={r}>{r}</li>))}
                        </ul>
                    </div>
                    <div className="card-soft p-6" style={{ background: "rgba(180,83,9,0.06)" }}>
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5" style={{ color: "var(--warning)" }} />
                            <h2 className="font-display text-lg font-semibold text-[color:var(--brand-navy)]">
                                {t("bowelPrep.detail.redFlagsTitle")}
                            </h2>
                        </div>
                        <ul className="mt-3 space-y-2 text-sm text-[color:var(--text-muted)] leading-relaxed pl-5 list-disc marker:text-[color:var(--warning)]">
                            {redFlagsList.map((r) => (<li key={r}>{r}</li>))}
                        </ul>
                        <p className="mt-3 flex items-center gap-1.5 text-sm text-[color:var(--brand-navy)] font-semibold">
                            <Phone className="h-4 w-4" /> {CLINIC_PHONE}
                        </p>
                    </div>
                </div>
            </section>

            <section className="container-page pb-10">
                <div
                    className="card-soft p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                    style={{ background: "linear-gradient(135deg, rgba(14,124,134,0.10) 0%, rgba(255,255,255,1) 60%)" }}
                >
                    <div>
                        <p className="font-display text-lg font-semibold text-[color:var(--brand-navy)]">
                            {t("bowelPrep.detail.questionsTitle")}
                        </p>
                        <p className="mt-1 text-sm text-[color:var(--text-muted)] leading-relaxed">
                            {t("bowelPrep.detail.questionsBody")}
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                        <a
                            href={`tel:${CLINIC_PHONE.replace(/[^\d+]/g, "")}`}
                            className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-[color:var(--brand-navy)] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[color:var(--brand-navy-2)]"
                        >
                            <Phone className="h-4 w-4" />
                            {t("bowelPrep.detail.callOfficeButton", { phone: CLINIC_PHONE })}
                        </a>
                        <Link
                            to="/resources/bowel-prep-guide"
                            className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-[color:var(--border-default)] bg-white px-4 py-2.5 text-sm font-semibold text-[color:var(--brand-navy)] hover:bg-[color:var(--bg-subtle)]"
                        >
                            {t("cta.bothPrepGuides")}
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
